/**
 * "Addresses" on a service's Apps page: where else the running service is reached besides its preview — its other
 * named ports (`ports` in `.flow/service.json`, an Open link each; a TCP one is for the other services only), the other
 * services of its Docker Compose app that publish a port (an Open link each), and, for a worker, that it has no web page.
 * From the runner's status of the session (utils/serviceAddressesModel). Nothing when there is nothing more to say.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { hasOtherAddresses, protocolLabel, serviceAddresses } from '../../utils/serviceAddressesModel';
import { Card } from './ServiceTabs/common';

function OpenLink({ url, testId }) {
  return <a className="apps-info-link" data-testid={testId} href={url} rel="noopener noreferrer" target="_blank">Open ↗</a>;
}
OpenLink.propTypes = { testId: PropTypes.string, url: PropTypes.string.isRequired };

export default function ServiceAddresses({ info = null }) {
  const addresses = serviceAddresses(info);
  if (!hasOtherAddresses(addresses)) { return null; }
  return (
    <Card testId="apps-info-addresses" title="Addresses">
      {addresses.worker ? (
        <p data-testid="apps-worker-note">A worker: it runs in the background with no port and no web page. See what it does in Logs.</p>
      ) : (
        <ul className="apps-endpoints">
          {addresses.main && (
            <li data-testid="apps-address-main">
              <strong>Preview</strong> · {protocolLabel(addresses.main.protocol)}
              {addresses.main.protocol === 'grpc' && <span className="apps-muted"> (browsers and the API tab call it through gRPC-Web)</span>}
              {addresses.main.protocol === 'tcp' && <span className="apps-muted"> (the other services reach it; a browser cannot)</span>}
            </li>
          )}
          {addresses.ports.map((port) => (
            <li data-testid={`apps-address-port-${port.name}`} key={port.name}>
              <strong>{port.label}</strong>{' '}
              {port.url ? <OpenLink testId={`apps-open-port-${port.name}`} url={port.url} /> : <span className="apps-muted">for the other services only</span>}
            </li>
          ))}
          {addresses.compose.map((item) => (
            <li data-testid={`apps-address-compose-${item.service}`} key={item.service}>
              <strong>{item.service}</strong> · compose service, port {item.port}{' '}
              {item.url && <OpenLink testId={`apps-open-compose-${item.service}`} url={item.url} />}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

ServiceAddresses.propTypes = {
  // The runner's status of the service's session (previewUrl, kind, protocol, ports, composeEntry).
  info: PropTypes.object,
};
